import React from "react";
import PropTypes from "prop-types";

// material-ui
import InputBase from "@material-ui/core/InputBase";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import { alpha, withStyles, makeStyles } from "@material-ui/core/styles";
import CheckBoxGroup from "./CheckBoxGroup";
import { Box, IconButton, Typography } from "@material-ui/core";

// components
import { WithCopyToClipboard } from "./withCopyToClipboard";

const useStyles = makeStyles((theme) => ({
	formController: {
		margin: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			margin: 0,
		},
	},
    copySectionIconRoot: {
        padding: 0,
    },
    copyToClipboardBar: {
        display: "flex",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginTop: theme.spacing(3),
        "& > * + *": {
            display: "flex",
            gap: theme.spacing(1),
        },
    },
}));

const BootstrapInput = withStyles((theme) => ({
	root: {
		"label + &": {
			marginTop: theme.spacing(2.5),
		},
	},
	input: {
		borderRadius: 10,
		position: "relative",
		backgroundColor: theme.palette.common.white,
		border: "1px solid #ced4da",
		fontSize: 16,
		width: "100%",
		padding: "10px 12px",
		transition: theme.transitions.create(["border-color", "box-shadow"]),
		"&:focus": {
			boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
			borderColor: theme.palette.primary.main,
		},
	},
}))(InputBase);

const CopyToClipboardBar = WithCopyToClipboard(({ label, handleCopyToClipboard }) => {
    const classes = useStyles();
    const handleClick = () => {
        handleCopyToClipboard();
    };

    return (
        <Box className={classes.copyToClipboardBar}>
            <Typography variant="h6">{label}</Typography>
            <Box>
                <Typography variant="h6">{"Copy"}</Typography>
                <IconButton
                    classes={{ root: classes.copySectionIconRoot }}
                    variant="normal"
                    onClick={handleClick}
                >
                    <svg
                        width="16px"
                        height="16px"
                        viewBox="0 0 14 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M1.64706 16C1.19412 16 0.806373 15.8433 0.483824 15.53C0.161275 15.2167 0 14.84 0 14.4V3.2H1.64706V14.4H10.7059V16H1.64706ZM4.94118 12.8C4.48824 12.8 4.10049 12.6433 3.77794 12.33C3.45539 12.0167 3.29412 11.64 3.29412 11.2V1.6C3.29412 1.16 3.45539 0.783333 3.77794 0.47C4.10049 0.156667 4.48824 0 4.94118 0H12.3529C12.8059 0 13.1936 0.156667 13.5162 0.47C13.8387 0.783333 14 1.16 14 1.6V11.2C14 11.64 13.8387 12.0167 13.5162 12.33C13.1936 12.6433 12.8059 12.8 12.3529 12.8H4.94118Z"
                            fill="#6886EF"
                        ></path>
                    </svg>
                </IconButton>
            </Box>
        </Box>
    );
});

export default function InputBox({
	label,
	onChange,
	onKeyDown,
	defaultValue,
	classes,
	multiline,
	maxRows,
	rows,
	type,
	endAdornment,
	showCheckBoxOptions,
	checkBoxOptions,
	onCheck,
    value,
    disabled = false,
    clipboardBarLabel
}) {
	const _classes = useStyles();
	const multilineProps = {};
	if (multiline) {
		multilineProps.multiline = multiline;
		multilineProps.minRows = rows;
		multilineProps.maxRows = maxRows;
	}
	return (
        <FormControl className={`${_classes.formController} ${classes.cInputBoxContainer}`}>
            {label && (
                <InputLabel className={classes.cInputLabel} shrink htmlFor={`${label}_id`}>
                    {label}
                </InputLabel>
            )}
            {showCheckBoxOptions && (
                <>
                    <CheckBoxGroup
                        onCheck={onCheck}
                        checkBoxOptions={checkBoxOptions}
                        resetSelectedOptions={!defaultValue}
                    />
                    <CopyToClipboardBar textToCopy={value} label={clipboardBarLabel || ""} />
                </>
            )}
            <BootstrapInput
                className={classes.cInputBox}
                onChange={onChange}
                onKeyDown={onKeyDown}
                defaultValue={defaultValue}
                value={value}
                endAdornment={endAdornment}
                id={`${label}_id`}
                type={type}
                disabled={disabled}
                {...multilineProps}
            />
        </FormControl>
    );
}

InputBox.propTypes = {
	label: PropTypes.string,
	onChange: PropTypes.func,
	onKeyDown: PropTypes.func,
	defaultValue: PropTypes.any,
	type: PropTypes.string,
	classes: PropTypes.shape({
		cInputLabel: PropTypes.string,
		cInputBox: PropTypes.string,
		cInputBoxContainer: PropTypes.string,
	}),
	multiline: PropTypes.bool,
	maxRows: PropTypes.number,
	rows: PropTypes.number,
	endAdornment: PropTypes.func,
	showCheckBoxOptions: PropTypes.bool,
	onCheck: PropTypes.func,
    checkBoxOptions: PropTypes.array,
    value: PropTypes.string,
    clipboardBarLabel: PropTypes.string,
    disabled: PropTypes.bool,
};
InputBox.defaultProps = {
	onChange: () => {},
	onKeyDown: () => {},
	defaultValue: " ",
	classes: {},
	multiline: false,
	type: "input",
	endAdornment: null,
	showCheckBoxOptions: false,
};
